import React, { useState, useEffect, useCallback } from "react";
import { scrollToTop } from "Utils";
import { toast } from "react-toastify";
import OrganizationServices from "services/OrganizationServices";
import {StatusChip,ConfirmDialog,TextValidator,MainCard,Button,CollapsibleSection,ValidatorForm,Box,TableTopSearch,MuiTable} from "components";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import { IconButton, Tooltip,CircularProgress } from "@mui/material";
import ColorPickerValidator from "components/ColorPicker";
import Grid from "@mui/material/Grid";


const AddOrganization = () => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const handleEditOrganization = (org) => {
    setFormData({
      organizationName: org.name || "",
      organizationNameSinhala: org.sinhala_name || "",
      organizationNameTamil: org.tamil_name || "",
      sort: org.short_name || "",
      color_code: org.color_code || "#000000",
    });
    setShortNameError("");
    setIsEdit(true);
    setEditingId(org.id);
    setOpenEditModal(true);
    scrollToTop();
  };
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const handleChangeStatus = async (id, status) => {
    setStatusLoadingId(id);
    const actionText =
      status === "Active"
        ? "deactivate this organization"
        : "activate this organization";
    ConfirmDialog({
      type: status === "Active" ? "error" : "success",
      title: `Are you sure you want to ${actionText}?`,
      description:
        status === "Active"
          ? "This will deactivate the organization. You can reactivate it later."
          : "This will activate the organization. You can deactivate it later.",
      buttons: [
        {
          label: "No",
          onClick: () => {
            setStatusLoadingId(null);
            toast.info("Action cancelled.");
          },
        },
        {
          label: "Yes",
          onClick: async () => {
            const newStatus = status === "Active" ? "Inactive" : "Active";
            try {
              const response = await OrganizationServices.updateStatusByPut(
                id,
                { status: newStatus }
              );
              if (response.success) {
                toast.success(`Organization status changed to ${newStatus}!`);
                await fetchOrganizations();
              } else {
                toast.error("Failed to update status.");
              }
            } catch (err) {
              toast.error("Error updating status. Please try again.");
            }
            setStatusLoadingId(null);
          },
        },
      ],
    });
  };
  const [formData, setFormData] = useState({
    organizationName: "",
    organizationNameSinhala: "",
    organizationNameTamil: "",
    sort: "",
    color_code: "#000000",
  });

  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [shortNameError, setShortNameError] = useState("");
  const [filters, setFilters] = useState({
    page: 0,
    limit: 10,
    search: "",
    "order[0]": ["updatedAt", "DESC"],
  });

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OrganizationServices.get(filters);
      const orgs =
        response?.data?.organizations?.data ||
        response?.data?.organizations ||
        response?.data;
      const total =
        response?.data?.organizations?.totalItems ||
        response?.data?.organizations?.total ||
        (Array.isArray(orgs) ? orgs.length : 0);
      if (Array.isArray(orgs)) {
        setOrganizations(orgs);
        setTotalItems(total);
      } else {
        setOrganizations([]);
        setTotalItems(0);
      }
    } catch (error) {
      setOrganizations([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const setPage = (page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: page - 1,
    }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 0,
      limit: pageSize,
    }));
  };

  const checkShortNameExists = (shortName, excludeId = null) => {
    return organizations.some(org => 
      org.short_name?.toLowerCase() === shortName.toLowerCase() && 
      org.id !== excludeId
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'sort') {
      setShortNameError("");
    }
    
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleColorChange = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      color_code: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const shortName = formData.sort.trim();
    const excludeId = isEdit ? editingId : null;
    
    if (checkShortNameExists(shortName, excludeId)) {
      // setShortNameError("This short name already exists. Please choose a different one.");
      toast.error("Short name already exists!");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        name: formData.organizationName.trim(),
        sinhala_name: formData.organizationNameSinhala.trim(),
        tamil_name: formData.organizationNameTamil.trim(),
        short_name: shortName,
        color_code: formData.color_code.trim(),
      };
      let result;
      if (isEdit && editingId) {
        result = await OrganizationServices.update(editingId, payload);
        if (result.success) {
          toast.success("Organization updated successfully!");
        } else {
          toast.error("Failed to update organization.");
        }
      } else {
        result = await OrganizationServices.create(payload);
        if (
          result.success ||
          result.data ||
          (!result.error && !result.success)
        ) {
          toast.success("The organization is successfully created.");
        } else {
          toast.error("Failed to create organization.");
        }
      }
      setFormData({
        organizationName: "",
        organizationNameSinhala: "",
        organizationNameTamil: "",
        sort: "",
        color_code: "#000000",
      });
      setShortNameError("");
      setIsEdit(false);
      setEditingId(null);
      setOpenEditModal(false);
      await fetchOrganizations();
      scrollToTop();
    } catch (error) {
      console.error("Error submitting organization:", error);
      toast.error("Failed to submit organization. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MainCard>
        <CollapsibleSection
          title="Add New Organization"
          buttonText="Add New Organization"
          collapsTrigger={openEditModal}
        >
          <ValidatorForm
            onSubmit={handleSubmit}
            process={loading}
            sx={{ flex: 1 }}
          >
            <Grid container spacing={1} sx={{ mb: 3 }}>
              <Grid size={{ lg: 4, md: 6, sm: 6, xs: 12 }}>
                <Box className="global-border" sx={{ p: 2 }}>
                  <Box
                    sx={{
                      textAlign: "start",
                      fontWeight: 600,
                      fontSize: 16,
                      mb: 1,
                    }}
                  >
                    English
                  </Box>
                  <TextValidator
                    label="Organization name"
                    id="organizationName"
                    placeholder="Name"
                    name="organizationName"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={formData.organizationName}
                    onChange={handleChange}
                    validators={["required", "maxStringLength:100"]}
                    errorMessages={[
                      "Organization name is required",
                      "The organization name cannot exceed 100 letters",
                    ]}
                    disabled={loading}
                    required={true}
                  />
                </Box>
              </Grid>
              <Grid size={{ lg: 4, md: 6, sm: 6, xs: 12 }}>
                <Box className="global-border" sx={{ borderRadius: 2, p: 2 }}>
                  <Box
                    sx={{
                      textAlign: "start",
                      fontWeight: 600,
                      fontSize: 16,
                      mb: 1,
                    }}
                  >
                    Sinhala
                  </Box>
                  <TextValidator
                    label="Organization name (ආයතනයේ නාමය)"
                    id="organizationNameSinhala"
                    placeholder="Name (Sinhala)"
                    name="organizationNameSinhala"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={formData.organizationNameSinhala || ""}
                    onChange={handleChange}
                    validators={["required", "maxStringLength:100"]}
                    errorMessages={[
                      "Organization name (Sinhala) is required",
                      "The organization name cannot exceed 100 letters",
                    ]}
                    disabled={loading}
                    required={true}
                  />
                </Box>
              </Grid>
              <Grid size={{ lg: 4, md: 6, sm: 6, xs: 12 }}>
                <Box className="global-border" sx={{ borderRadius: 2, p: 2 }}>
                  <Box
                    sx={{
                      textAlign: "start",
                      fontWeight: 600,
                      fontSize: 16,
                      mb: 1,
                    }}
                  >
                    Tamil
                  </Box>
                  <TextValidator
                    label="Organization name (அமைப்பின் பெயர்)"
                    id="organizationNameTamil"
                    placeholder="Name (Tamil)"
                    name="organizationNameTamil"
                    variant="outlined"
                    value={formData.organizationNameTamil || ""}
                    onChange={handleChange}
                    validators={["required", "maxStringLength:100"]}
                    errorMessages={[
                      "Organization name (Tamil) is required",
                      "The organization name cannot exceed 100 letters",
                    ]}
                    disabled={loading}
                    required={true}
                  />
                </Box>
              </Grid>
            </Grid>
            <Grid container spacing={1} sx={{ mb: 2, mt: 2 }}>
              <Grid size={{ lg: 4, md: 6, sm: 6, xs: 12 }}>
                <TextValidator
                  label="Short name"
                  id="sort"
                  placeholder="Short name"
                  name="sort"
                  variant="outlined"
                  value={formData.sort || ""}
                  onChange={handleChange}
                  validators={["required", "maxStringLength:15"]}
                  errorMessages={[
                    "Organization short name is required",
                    "The short name is too long. Please shorten it",
                  ]}
                  disabled={loading}
                  required={true}
                  error={!!shortNameError}
                  helperText={shortNameError}
                />
              </Grid>
              <Grid size={{ lg: 4, md: 6, sm: 6, xs: 12 }}>
                <ColorPickerValidator
                  label="Color"
                  name="color_code"
                  value={formData.color_code || ""}
                  onChange={handleColorChange}
                  validators={[
                    "required",
                    "matchRegexp:^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$",
                  ]}
                  errorMessages={[
                    "Color is required",
                    "Must be valid color code",
                  ]}
                  required={true}
                  disabled={loading}
                />
              </Grid>
            </Grid>
            <Grid className="mt-2" container spacing={1}>
              <Grid>
                <Button type="submit" process={loading}>
                  {isEdit ? "UPDATE" : "SAVE"}
                </Button>
              </Grid>
              <Grid>
                {openEditModal && (
                  <Button
                    className="btn-warning"
                    onClick={() => {
                      setFormData({
                        organizationName: "",
                        organizationNameSinhala: "",
                        organizationNameTamil: "",
                        sort: "",
                        color_code: "#000000",
                      });
                      setShortNameError("");
                      setIsEdit(false);
                      setEditingId(null);
                      setOpenEditModal(false);
                      scrollToTop();
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Grid>
            </Grid>
          </ValidatorForm>
        </CollapsibleSection>
        
          <TableTopSearch
            title="Organizations"
            showSearch={true}
            showTotalWithTitle={true}
            rowCount={totalItems}
            onSubmitSearch={null}
            onSearchChange={(e) => {
              setFilters(prevData => ({
                ...prevData,
                search: e.target.value
              }));
            }}
          />
        
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <MuiTable
              columns={[
                {
                  accessorKey: "name",
                  header: "Name",
                  Cell: ({ row }) => <span>{row.original.name || ""}</span>,
                },
                {
                  accessorKey: "sinhala_name",
                  header: "Sinhala Name",
                  Cell: ({ row }) => (
                    <span>{row.original.sinhala_name || ""}</span>
                  ),
                },
                {
                  accessorKey: "tamil_name",
                  header: "Tamil Name",
                  Cell: ({ row }) => (
                    <span>{row.original.tamil_name || ""}</span>
                  ),
                },
                {
                  accessorKey: "short_name",
                  header: "Short Name",
                  Cell: ({ row }) => (
                    <span>{row.original.short_name || ""}</span>
                  ),
                },
                {
                  accessorKey: "color_code",
                  header: "Color",
                  Cell: ({ cell }) => (
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        backgroundColor: cell.getValue(),
                        borderRadius: 4,
                        border: "1px solid #ccc",
                        display: "inline-block",
                      }}
                    />
                  ),
                },
                {
                  accessorKey: "status",
                  header: "Status",
                  Cell: ({ row }) => {
                    const status = row.original.status || "Inactive";
                    return (
                      <span
                        style={{ cursor: "pointer" }}
                        title={
                          status === "Active"
                            ? "Click to set Inactive"
                            : "Click to set Active"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChangeStatus(row.original.id, status);
                        }}
                      >
                        {statusLoadingId === row.original.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <StatusChip status={status} />
                        )}
                      </span>
                    );
                  },
                },
                {
                  accessorKey: "createdAt",
                  header: "Created Date",
                  Cell: ({ row }) => (
                    <span>
                      {row.original.createdAt
                        ? new Date(row.original.createdAt).toLocaleDateString()
                        : ""}
                    </span>
                  ),
                },
                {
                  accessorKey: "actions",
                  header: "Actions",
                  Cell: ({ cell }) => (
                    <div style={{ display: "flex", gap: 8 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() =>
                            handleEditOrganization(cell.row.original)
                          }
                        >
                          <DriveFileRenameOutlineOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  ),
                  headerProps: { style: { textAlign: "right" } },
                  cellProps: { style: { textAlign: "right" } },
                },
              ]}
              data={organizations}
              title="All Organizations"
              manualPagination={true}
              rowCount={totalItems}
              currentPage={filters.page}
              pageSize={filters.limit}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
            />
          </Grid>
        </Grid>
      </MainCard>
    </>
  );
};

export default AddOrganization;
